/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/utils/formatEventDate.ts
 *
 * Rôle :
 *   LOT 3 (socle d'identité) : Formatage FR de la DATE de l'événement, DÉRIVÉ
 *   de la source autoritaire `tne_events` (via useEventConfig : `starts_at` /
 *   `ends_at`). Frère jumeau de `formatEventTime.ts` (qui, lui, dérive l'HEURE) :
 *   même philosophie, même robustesse, on ne fige plus « 9 octobre 2026 » en dur
 *   dans le catalogue i18n, la date affichée suit TOUJOURS la base, réglée au
 *   back-office /admin/event-config. C'est la brique qui alimente les tokens
 *   d'interpolation `{eventDate}` des clés i18n migrées (LOT 3, 4, 5).
 *
 * Pourquoi un parsing littéral (regex + Date.UTC) et pas `new Date(str)` :
 *   `starts_at`/`ends_at` arrivent au format local Paris « YYYY-MM-DD HH:MM:SS »
 *   (ou ISO), SANS fuseau explicite. `new Date('2026-10-09 08:30:00')` est
 *   interprété différemment selon le moteur (UTC vs local) → décalage possible
 *   d'un jour à minuit. On extrait donc Y-M-D littéralement, et le jour de la
 *   semaine se calcule via `Date.UTC(...)` (aucun décalage de fuseau, 100 %
 *   déterministe), même précaution que `formatEventTime.ts`.
 *
 * Réutilisable : 100 %, toute app DRANE / tout salon affichant une date FR.
 *   Pour un salon SUR PLUSIEURS JOURS, la forme « du 9 au 11 octobre 2026 » est
 *   gérée nativement (un 2ᵉ salon n'aura pas forcément une date unique).
 *
 * Voir : hooks/useEventConfig.ts (source des dates), utils/formatEventTime.ts
 *        (modèle), context/EventIdentityProvider.tsx (consommateur),
 *        app/docs/sprints/LOT-3-socle-identite.md.
 *
 * @author Mikael
 * @since  2026-07-23 (LOT 3, socle d'identité)
 */

/**
 * Formes de rendu de la date, du plus complet au plus compact :
 *   - `'long'`   → « vendredi 9 octobre 2026 » (jour de semaine inclus)
 *   - `'medium'` → « 9 octobre 2026 »
 *   - `'short'`  → « 9 oct. »
 */
export type EventDateFormat = 'long' | 'medium' | 'short';

/** Forme structurée d'une date décomposée (année / mois 1-12 / jour 1-31). */
interface DateParts {
  readonly y: number;
  readonly m: number;
  readonly d: number;
}

/** Mois FR en toutes lettres, index 0 = janvier (aligné sur `m - 1`). */
const MOIS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;

/** Mois FR abrégés (forme `short`), « mars », « mai », « juin » restent pleins. */
const MOIS_FR_ABR = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
] as const;

/** Jours de la semaine FR, index = valeur de `Date.getUTCDay()` (0 = dimanche). */
const JOURS_FR = [
  'dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi',
] as const;

/**
 * Extrait { année, mois, jour } d'un horodatage local « YYYY-MM-DD… ».
 * Ignore volontairement l'heure et le fuseau (cf. en-tête « pourquoi »).
 *
 * @param datetime Horodatage local (ou null/undefined).
 * @returns Les composantes de date, ou `null` si non parsable / invalide.
 */
function parseYmd(datetime: string | null | undefined): DateParts | null {
  if (typeof datetime !== 'string') return null;
  const match = /(\d{4})-(\d{2})-(\d{2})/.exec(datetime);
  if (!match) return null;
  const y = Number.parseInt(match[1]!, 10);
  const m = Number.parseInt(match[2]!, 10);
  const d = Number.parseInt(match[3]!, 10);
  // Garde-fous : un mois/jour hors bornes signale une donnée corrompue → null
  // plutôt qu'un « 9 undefined 2026 » silencieux.
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/** Vrai si les deux dates désignent le même jour calendaire. */
function memeJour(a: DateParts, b: DateParts): boolean {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

/** Nom du jour de la semaine FR, calculé en UTC (aucun décalage de fuseau). */
function jourSemaine(p: DateParts): string {
  return JOURS_FR[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]!;
}

/** Rend UNE date (jour unique) selon la forme demandée. */
function rendreJourUnique(p: DateParts, format: EventDateFormat): string {
  if (format === 'short') return `${p.d} ${MOIS_FR_ABR[p.m - 1]}`;
  if (format === 'long') return `${jourSemaine(p)} ${p.d} ${MOIS_FR[p.m - 1]} ${p.y}`;
  return `${p.d} ${MOIS_FR[p.m - 1]} ${p.y}`; // medium
}

/**
 * Rend un INTERVALLE de dates « du … au … » en factorisant ce qui est commun
 * (le mois si identique, l'année si identique). La forme `long` n'ajoute pas
 * le jour de la semaine sur un intervalle (trop lourd : « du vendredi 9 au
 * dimanche 11… »), elle se comporte comme `medium`, choix documenté.
 */
function rendreIntervalle(a: DateParts, b: DateParts, format: EventDateFormat): string {
  const mois = format === 'short' ? MOIS_FR_ABR : MOIS_FR;
  // Cas le plus courant : même mois et même année → « du 9 au 11 octobre 2026 ».
  if (a.y === b.y && a.m === b.m) {
    const annee = format === 'short' ? '' : ` ${a.y}`;
    return `du ${a.d} au ${b.d} ${mois[a.m - 1]}${annee}`;
  }
  // Même année, mois différents → « du 30 octobre au 2 novembre 2026 ».
  if (a.y === b.y) {
    const annee = format === 'short' ? '' : ` ${a.y}`;
    return `du ${a.d} ${mois[a.m - 1]} au ${b.d} ${mois[b.m - 1]}${annee}`;
  }
  // Années différentes → on explicite les deux années.
  return `du ${a.d} ${mois[a.m - 1]} ${a.y} au ${b.d} ${mois[b.m - 1]} ${b.y}`;
}

/**
 * Construit la date FR de l'événement à partir de ses bornes autoritaires.
 * Tolérant (offline-first §18) : si seule la date de début est connue, rend un
 * jour unique ; si aucune borne n'est parsable, rend `null` (le consommateur
 * masque alors la date, ou retombe sur une chaîne vide).
 *
 * @param config Bornes de l'événement (`starts_at` / `ends_at`).
 * @param format Forme demandée (défaut `'medium'` = « 9 octobre 2026 »).
 * @returns La date FR (jour unique ou intervalle), ou `null` si indéterminable.
 *
 * @example formatEventDateFr({ starts_at: '2026-10-09 08:30:00' })            // '9 octobre 2026'
 * @example formatEventDateFr({ starts_at: '2026-10-09' }, 'long')             // 'vendredi 9 octobre 2026'
 * @example formatEventDateFr({ starts_at: '2026-10-09' }, 'short')            // '9 oct.'
 * @example formatEventDateFr({ starts_at: '2026-10-09', ends_at: '2026-10-11' }) // 'du 9 au 11 octobre 2026'
 */
export function formatEventDateFr(
  config: { readonly starts_at?: string | null; readonly ends_at?: string | null },
  format: EventDateFormat = 'medium',
): string | null {
  const start = parseYmd(config.starts_at);
  if (!start) return null; // sans début, pas de date affichable
  const end = parseYmd(config.ends_at);
  // Jour unique : pas de fin, ou fin == début.
  if (!end || memeJour(start, end)) return rendreJourUnique(start, format);
  return rendreIntervalle(start, end, format);
}
